<script setup lang="ts">
import { tv, type VariantProps } from 'tailwind-variants/lite'
import { computed, useTemplateRef, type Component } from 'vue'
import { LSpinnerIcon } from '../../icons'
import { LIcon } from '../icon'
import LBaseButton from './BaseButton.vue'
import type { BaseButtonEmits, BaseButtonProps } from './types'

const style = tv({
  base: 'lui-button',
  slots: {
    busyIndicator: 'lui-button__busy-indicator',
    content: 'lui-button__content',
  },
  variants: {
    busy: {
      true: 'lui-button--busy',
    },
    iconPosition: {
      start: 'lui-button--icon-position-start',
      end: 'lui-button--icon-position-end',
    },
    size: {
      sm: 'lui-button--size-sm',
      md: 'lui-button--size-md',
      lg: 'lui-button--size-lg',
    },
    tone: {
      primary: 'lui-button--tone-primary',
      secondary: 'lui-button--tone-secondary',
      tertiary: 'lui-button--tone-tertiary',
      danger: 'lui-button--tone-danger',
      invert: 'lui-button--tone-invert',
    },
    variant: {
      solid: 'lui-button--variant-solid',
      outline: 'lui-button--variant-outline',
      ghost: 'lui-button--variant-ghost',
      link: 'lui-button--variant-link',
    },
  },
})

export interface ButtonProps extends BaseButtonProps {
  icon?: Component
  iconPosition?: 'start' | 'end'
  size?: VariantProps<typeof style>['size']
  tone?: VariantProps<typeof style>['tone']
  variant?: VariantProps<typeof style>['variant']
}

export type ButtonEmits = BaseButtonEmits

const props = withDefaults(defineProps<ButtonProps>(), {
  iconPosition: 'start',
  size: 'md',
  tone: 'primary',
  variant: 'solid',
})

const emit = defineEmits<ButtonEmits>()

const ref = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>('ref')

defineExpose({
  focus() {
    ref.value?.focus()
  },
})

const classes = computed(() =>
  style({
    busy: props.busy,
    iconPosition: props.iconPosition,
    size: props.size,
    tone: props.tone,
    variant: props.variant,
  }),
)

const handleClick = (event: MouseEvent) => {
  emit('click', event)
}
</script>

<template>
  <LBaseButton
    :is="is"
    ref="ref"
    :class="classes.base()"
    :disabled="disabled"
    :busy="busy"
    :type="type"
    @click="handleClick"
  >
    <span :class="classes.busyIndicator()" aria-live="assertive">
      <LIcon :is="LSpinnerIcon" v-if="busy" aria-label="Busy" />
    </span>

    <span :class="classes.content()">
      <LIcon :is="icon" v-if="icon" />
      <slot />
    </span>
  </LBaseButton>
</template>

<style>
@reference "../../css/tailwind.css";

@layer components {
  .lui-button {
    @apply relative inline-flex cursor-pointer items-center justify-center align-middle whitespace-nowrap disabled:pointer-events-none;
  }

  .lui-button--busy {
    @apply pointer-events-none;
  }

  .lui-button__busy-indicator {
    @apply absolute flex items-center;
  }

  .lui-button__content {
    @apply flex items-center;
  }

  .lui-button--busy .lui-button__content {
    @apply opacity-0;
  }

  .lui-button--icon-position-end .lui-button__content {
    @apply flex-row-reverse;
  }
}
</style>
